import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createCodOrder } from "@/lib/orders.functions";
import { storeConfirmedOrder } from "@/lib/order-confirmation";
import { formatDH, useShop } from "@/lib/shop-store";

const orderSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, "Veuillez entrer votre nom et prénom")
    .max(100, "Le nom est trop long"),
  phone: z
    .string()
    .trim()
    .min(1, "Veuillez entrer votre numéro de téléphone")
    .regex(
      /^(\+212\s?|0)[5-7](\s?\d{2}){4}$/,
      "Numéro de téléphone invalide (ex. 06 12 34 56 78)",
    ),
  city: z
    .string()
    .trim()
    .min(1, "Veuillez entrer votre ville")
    .max(100, "Le nom de la ville est trop long"),
  address: z
    .string()
    .trim()
    .min(1, "Veuillez entrer votre adresse")
    .max(300, "L'adresse est trop longue"),
});

type OrderFormValues = z.infer<typeof orderSchema>;

/**
 * Formulaire de commande (paiement à la livraison).
 * À la validation, une VRAIE commande Shopify est créée côté serveur
 * (Admin API `orderCreate`) : elle apparaît immédiatement dans Shopify Admin > Orders.
 */
export function QuickOrderDialog() {
  const { codOpen, setCodOpen, codLines, clearCart } = useShop();
  const submitOrder = useServerFn(createCodOrder);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<OrderFormValues>({
    resolver: zodResolver(orderSchema),
    defaultValues: { fullName: "", phone: "", city: "", address: "" },
  });

  const total = codLines.reduce((n, l) => n + l.price * l.quantity, 0);
  const summary = codLines
    .map((l) => `${l.name}${l.variant ? ` · ${l.variant}` : ""} × ${l.quantity}`)
    .join(" · ");

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
      setError(null);
    }
    setCodOpen(open);
  };

  const onSubmit = async (values: OrderFormValues) => {
    setError(null);
    try {
      const res = await submitOrder({
        data: {
          ...values,
          lines: codLines.map((l) => ({
            variantId: l.merchandiseId,
            quantity: l.quantity,
          })),
        },
      });
      // Shopify a créé ET vérifié la commande côté serveur : on peut confirmer.
      storeConfirmedOrder({
        orderNumber: res.orderNumber,
        fullName: values.fullName,
        city: values.city,
        total,
        lines: codLines.map((l) => ({
          name: l.name,
          ...(l.variant ? { variant: l.variant } : {}),
          quantity: l.quantity,
          price: l.price,
        })),
        createdAt: Date.now(),
      });
      clearCart();
      form.reset();
      setCodOpen(false);
      void navigate({ to: "/commande-confirmee" });
    } catch (err) {
      console.error("Création commande Shopify:", err);
      const ref =
        err instanceof Error ? err.message.match(/correlationId=([\w-]+)/)?.[1] : undefined;
      setError(
        ref
          ? `La commande n'a pas pu être enregistrée. Merci de réessayer (réf. ${ref}).`
          : "La commande n'a pas pu être enregistrée. Merci de réessayer.",
      );
    }
  };

  return (
    <Dialog open={codOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md px-5 py-6 sm:px-6">
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-2xl font-light tracking-tight">
            Finaliser votre commande
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {`${summary} · ${formatDH(total)} · Paiement à la livraison`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-xs">Nom et prénom</FormLabel>
                    <FormControl>
                      <Input placeholder="Votre nom complet" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-xs">Téléphone</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="06 XX XX XX XX" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-xs">Ville</FormLabel>
                    <FormControl>
                      <Input placeholder="Votre ville" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-xs">Adresse</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Votre adresse complète"
                        className="min-h-[80px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <DialogFooter className="mt-2">
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting || codLines.length === 0}
                  className="w-full"
                >
                  {form.formState.isSubmitting ? "Envoi…" : "Confirmer la commande"}
                </Button>
              </DialogFooter>
            </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
